import type { ReactNode } from 'react';
import type { IconName } from './SuperIcon.types';

interface PathProps {
  stroke: number;
  color?: string;
}

const lineProps = (p: PathProps) => ({
  fill: 'none',
  stroke: p.color ?? 'currentColor',
  strokeWidth: p.stroke,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
});

const filledProps = (color?: string) => ({
  fill: color ?? 'currentColor',
});

export type IconRenderer = (p: PathProps) => ReactNode;

const icons: Record<IconName, IconRenderer> = {
  home: (p) => (
    <>
      <path d="M3 11L12 4l9 7" {...lineProps(p)} />
      <path d="M5 10v10h14V10" {...lineProps(p)} />
    </>
  ),
  dashboard: (p) => (
    <>
      <rect x="3" y="3" width="7" height="9" rx="1.5" {...lineProps(p)} />
      <rect x="14" y="3" width="7" height="5" rx="1.5" {...lineProps(p)} />
      <rect x="14" y="12" width="7" height="9" rx="1.5" {...lineProps(p)} />
      <rect x="3" y="16" width="7" height="5" rx="1.5" {...lineProps(p)} />
    </>
  ),
  inbox: (p) => (
    <>
      <path d="M3 12l2.5-7a2 2 0 0 1 1.9-1.4h9.2a2 2 0 0 1 1.9 1.4L21 12" {...lineProps(p)} />
      <path d="M3 12h5l1.5 2h5L16 12h5v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6Z" {...lineProps(p)} />
    </>
  ),
  users: (p) => (
    <>
      <circle cx="9" cy="8" r="3.5" {...lineProps(p)} />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" {...lineProps(p)} />
      <path d="M16 11a3 3 0 1 0 0-6" {...lineProps(p)} />
      <path d="M17.5 20a6.5 6.5 0 0 0-3-5.5" {...lineProps(p)} />
    </>
  ),
  bot: (p) => (
    <>
      <rect x="4" y="7" width="16" height="12" rx="3" {...lineProps(p)} />
      <path d="M12 3v4" {...lineProps(p)} />
      <circle cx="9" cy="13" r="1" {...lineProps(p)} />
      <circle cx="15" cy="13" r="1" {...lineProps(p)} />
      <path d="M9.5 17h5" {...lineProps(p)} />
    </>
  ),
  book: (p) => (
    <>
      <path d="M4 4h11a3 3 0 0 1 3 3v13H7a3 3 0 0 1-3-3V4Z" {...lineProps(p)} />
      <path d="M4 17a3 3 0 0 1 3-3h11" {...lineProps(p)} />
    </>
  ),
  zap: (p) => <path d="M13 3L4 14h7l-1 7 9-11h-7l1-7Z" {...lineProps(p)} />,
  coin: (p) => (
    <>
      <ellipse cx="12" cy="6" rx="8" ry="3" {...lineProps(p)} />
      <path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6" {...lineProps(p)} />
      <path d="M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" {...lineProps(p)} />
    </>
  ),
  chart: (p) => (
    <>
      <path d="M3 3v18h18" {...lineProps(p)} />
      <path d="M7 15l4-5 3 3 5-7" {...lineProps(p)} />
    </>
  ),
  plug: (p) => (
    <>
      <path d="M9 2v4M15 2v4" {...lineProps(p)} />
      <path d="M6 6h12v6a6 6 0 0 1-12 0V6Z" {...lineProps(p)} />
      <path d="M12 18v4" {...lineProps(p)} />
    </>
  ),
  settings: (p) => (
    <>
      <circle cx="12" cy="12" r="3" {...lineProps(p)} />
      <path
        d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1A2 2 0 1 1 7 4.9l.1.1a1.7 1.7 0 0 0 1.8.3 1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8c.2.6.7 1 1.4 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z"
        {...lineProps(p)}
      />
    </>
  ),
  menu: (p) => (
    <>
      <path d="M4 7h16M4 12h16M4 17h16" {...lineProps(p)} />
    </>
  ),
  'more-horizontal': (p) => (
    <>
      <circle cx="6" cy="12" r="1.4" {...filledProps(p.color)} />
      <circle cx="12" cy="12" r="1.4" {...filledProps(p.color)} />
      <circle cx="18" cy="12" r="1.4" {...filledProps(p.color)} />
    </>
  ),
  'more-vertical': (p) => (
    <>
      <circle cx="12" cy="6" r="1.4" {...filledProps(p.color)} />
      <circle cx="12" cy="12" r="1.4" {...filledProps(p.color)} />
      <circle cx="12" cy="18" r="1.4" {...filledProps(p.color)} />
    </>
  ),
  search: (p) => (
    <>
      <circle cx="11" cy="11" r="7" {...lineProps(p)} />
      <path d="M20 20l-4-4" {...lineProps(p)} />
    </>
  ),
  filter: (p) => <path d="M3 5h18l-7 9v6l-4-2v-4L3 5Z" {...lineProps(p)} />,
  plus: (p) => <path d="M12 5v14M5 12h14" {...lineProps(p)} />,
  minus: (p) => <path d="M5 12h14" {...lineProps(p)} />,
  x: (p) => <path d="M6 6l12 12M18 6L6 18" {...lineProps(p)} />,
  check: (p) => <path d="M5 12l5 5L20 7" {...lineProps(p)} />,
  'chevron-up': (p) => <path d="M6 15l6-6 6 6" {...lineProps(p)} />,
  'chevron-down': (p) => <path d="M6 9l6 6 6-6" {...lineProps(p)} />,
  'chevron-left': (p) => <path d="M15 6l-6 6 6 6" {...lineProps(p)} />,
  'chevron-right': (p) => <path d="M9 6l6 6-6 6" {...lineProps(p)} />,
  'arrow-up': (p) => <path d="M12 19V5M5 12l7-7 7 7" {...lineProps(p)} />,
  'arrow-down': (p) => <path d="M12 5v14M5 12l7 7 7-7" {...lineProps(p)} />,
  'arrow-up-right': (p) => <path d="M7 17L17 7M9 7h8v8" {...lineProps(p)} />,
  eye: (p) => (
    <>
      <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z" {...lineProps(p)} />
      <circle cx="12" cy="12" r="3" {...lineProps(p)} />
    </>
  ),
  'eye-off': (p) => (
    <>
      <path d="M3 3l18 18" {...lineProps(p)} />
      <path
        d="M10.6 6.2A10 10 0 0 1 22 12s-2 3.5-5.3 5.6M6.5 6.5C3.6 8.2 2 12 2 12s4 7 10 7c1.9 0 3.6-.4 5-1.2"
        {...lineProps(p)}
      />
    </>
  ),
  edit: (p) => (
    <>
      <path d="M4 20h4l11-11-4-4L4 16v4Z" {...lineProps(p)} />
      <path d="M14 6l4 4" {...lineProps(p)} />
    </>
  ),
  trash: (p) => (
    <>
      <path d="M4 7h16M9 7V4h6v3M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" {...lineProps(p)} />
    </>
  ),
  copy: (p) => (
    <>
      <rect x="8" y="8" width="12" height="12" rx="2" {...lineProps(p)} />
      <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" {...lineProps(p)} />
    </>
  ),
  download: (p) => (
    <>
      <path d="M12 4v12M7 11l5 5 5-5" {...lineProps(p)} />
      <path d="M4 20h16" {...lineProps(p)} />
    </>
  ),
  upload: (p) => (
    <>
      <path d="M12 20V8M7 13l5-5 5 5" {...lineProps(p)} />
      <path d="M4 20h16" {...lineProps(p)} />
    </>
  ),
  'external-link': (p) => (
    <>
      <path d="M14 4h6v6" {...lineProps(p)} />
      <path d="M10 14L20 4" {...lineProps(p)} />
      <path d="M19 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" {...lineProps(p)} />
    </>
  ),
  link: (p) => (
    <>
      <path d="M10 14a5 5 0 0 0 7 0l3-3a5 5 0 1 0-7-7l-1 1" {...lineProps(p)} />
      <path d="M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" {...lineProps(p)} />
    </>
  ),
  refresh: (p) => (
    <>
      <path d="M20 11A8 8 0 0 0 5 7" {...lineProps(p)} />
      <path d="M4 13a8 8 0 0 0 15 4" {...lineProps(p)} />
      <path d="M20 4v5h-5M4 20v-5h5" {...lineProps(p)} />
    </>
  ),
  star: (p) => (
    <path
      d="M12 3l2.9 6 6.6 1-4.8 4.6 1.1 6.6L12 18l-5.8 3.2 1.1-6.6L2.5 10l6.6-1L12 3Z"
      {...lineProps(p)}
    />
  ),
  bookmark: (p) => <path d="M6 3h12v18l-6-4-6 4V3Z" {...lineProps(p)} />,
  bell: (p) => (
    <>
      <path d="M6 9a6 6 0 1 1 12 0v5l1.5 3h-15L6 14V9Z" {...lineProps(p)} />
      <path d="M10 19a2 2 0 0 0 4 0" {...lineProps(p)} />
    </>
  ),
  mail: (p) => (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" {...lineProps(p)} />
      <path d="M3 7l9 7 9-7" {...lineProps(p)} />
    </>
  ),
  info: (p) => (
    <>
      <circle cx="12" cy="12" r="9" {...lineProps(p)} />
      <path d="M12 8v.01M11 11h1v6h1" {...lineProps(p)} />
    </>
  ),
  success: (p) => (
    <>
      <circle cx="12" cy="12" r="9" {...lineProps(p)} />
      <path d="M8 12l3 3 5-6" {...lineProps(p)} />
    </>
  ),
  warning: (p) => (
    <>
      <path d="M12 3l10 18H2L12 3Z" {...lineProps(p)} />
      <path d="M12 10v5M12 18v.01" {...lineProps(p)} />
    </>
  ),
  error: (p) => (
    <>
      <circle cx="12" cy="12" r="9" {...lineProps(p)} />
      <path d="M9 9l6 6M15 9l-6 6" {...lineProps(p)} />
    </>
  ),
  sun: (p) => (
    <>
      <circle cx="12" cy="12" r="4" {...lineProps(p)} />
      <path
        d="M12 2v2M12 20v2M4 12H2M22 12h-2M5 5l1.5 1.5M17.5 17.5L19 19M5 19l1.5-1.5M17.5 6.5L19 5"
        {...lineProps(p)}
      />
    </>
  ),
  moon: (p) => <path d="M20 14a8 8 0 1 1-10-10 7 7 0 0 0 10 10Z" {...lineProps(p)} />,
  whatsapp: (p) => (
    <>
      <path d="M20 12a8 8 0 1 1-3.5-6.6L20 4l-1.5 3.6A8 8 0 0 1 20 12Z" {...lineProps(p)} />
      <path
        d="M9 9c.2 1.4 1 2.7 2.1 3.8 1.1 1 2.5 1.8 3.9 2 .4-.4 1-1.1 1.2-1.6-.6-.3-1.4-.6-2-.6-.3.3-.8.8-1.1.6-1-.5-1.9-1.4-2.4-2.4-.2-.3.3-.8.6-1.1 0-.6-.3-1.4-.6-2-.5.2-1.2.8-1.6 1.2"
        {...lineProps(p)}
      />
    </>
  ),
  instagram: (p) => (
    <>
      <rect x="3" y="3" width="18" height="18" rx="5" {...lineProps(p)} />
      <circle cx="12" cy="12" r="4" {...lineProps(p)} />
      <circle cx="17.5" cy="6.5" r="0.8" {...filledProps(p.color)} />
    </>
  ),
  facebook: (p) => (
    <path
      d="M14 4h-2a4 4 0 0 0-4 4v3H5v4h3v6h4v-6h3l1-4h-4V8a1 1 0 0 1 1-1h2V4Z"
      {...lineProps(p)}
    />
  ),
  telegram: (p) => <path d="M21 4L2 11l6 2 2 6 4-4 5 4 2-15Z" {...lineProps(p)} />,
  webchat: (p) => (
    <>
      <path d="M4 5h16v12H8l-4 4V5Z" {...lineProps(p)} />
      <path d="M8 11h8M8 14h5" {...lineProps(p)} />
    </>
  ),
  email: (p) => (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" {...lineProps(p)} />
      <path d="M3 7l9 7 9-7" {...lineProps(p)} />
    </>
  ),
  google: (p) => (
    <>
      <path d="M12 11v4h5.3a5 5 0 1 1-1.4-5.3" {...lineProps(p)} />
      <path d="M12 11h9" {...lineProps(p)} />
    </>
  ),
  microsoft: (p) => (
    <>
      <rect x="3" y="3" width="8" height="8" {...lineProps(p)} />
      <rect x="13" y="3" width="8" height="8" {...lineProps(p)} />
      <rect x="3" y="13" width="8" height="8" {...lineProps(p)} />
      <rect x="13" y="13" width="8" height="8" {...lineProps(p)} />
    </>
  ),
  sparkles: (p) => (
    <>
      <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3Z" {...lineProps(p)} />
      <path d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8L19 14Z" {...lineProps(p)} />
    </>
  ),
  tag: (p) => (
    <>
      <path d="M3 11V4h7l11 11-7 7L3 11Z" {...lineProps(p)} />
      <circle cx="8" cy="8" r="1" {...filledProps(p.color)} />
    </>
  ),
  send: (p) => <path d="M3 12L21 4l-7 18-3-7-8-3Z" {...lineProps(p)} />,
  paperclip: (p) => (
    <path
      d="M21 11l-9 9a5 5 0 0 1-7-7l9-9a3.5 3.5 0 0 1 5 5l-9 9a2 2 0 1 1-3-3l8-8"
      {...lineProps(p)}
    />
  ),
  note: (p) => (
    <>
      <path d="M5 4h10l4 4v12H5V4Z" {...lineProps(p)} />
      <path d="M15 4v4h4" {...lineProps(p)} />
      <path d="M8 12h8M8 16h6" {...lineProps(p)} />
    </>
  ),
};

export default icons;
